import { MouseEvent, useCallback, useEffect, useState } from "react";
import useApiRequests from "@services/http/useApiRequest";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { makeNotifs } from "@utils/makeNotifs";
import { IMinerDataCopyProps } from "./MinerDataCopy.props";
import { IMinerMeta } from "@services/changeMinerSettings";
import { useMinerContext } from "../../MinerContext";
import { useAuth } from "@utils/hooks/useAuth";
import { FullMinerInfoResponse, GetFullMinerInfo } from "@services/getMinerInfo";

export type MINER_DATA_TYPE = "IP" | "MAC" | "PASS";

const EXPIRED_KEY = "data:expired";

const isExpired = (id: number) => {
  const date = sessionStorage.getItem(`${EXPIRED_KEY}-${id}`);
  if (!date) return true;
  const now = new Date();
  return Number(date) < now.getTime();
};

export const useMinerDataCopyControl = (props: IMinerDataCopyProps) => {
  const { miner } = useMinerContext();
  const [loader, setLoader] = useState<undefined | MINER_DATA_TYPE | "ALL">();
  const [minerData, setMinerData] = useState<IMinerMeta>();
  const { ok } = makeNotifs();
  const { onSuccess } = useGlobalNotifs();
  const { enqueue } = useApiRequests();
  const auth = useAuth(); //useSelector((state: RootState) => state.auth);

  useEffect(() => {
    setMinerData(miner.meta);
  }, [miner.meta]);

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    onCopy(props.text);
  };

  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    onSuccess(ok.success);
  };

  const onCopyData = async (d: MINER_DATA_TYPE) => {
    setLoader(d);

    switch (d) {
      case "IP":
        onCopy(minerData?.ip || "");
        break;
      case "MAC":
        onCopy(minerData?.mac || "");
        break;

      case "PASS":
        onCopy(minerData?.password || "");
        break;

      default:
        break;
    }
    setTimeout(() => {
      setLoader(undefined);
      onSuccess(ok.success);
    }, 500);
  };

  const getMinerData = useCallback(async () => {
    const res = await enqueue<FullMinerInfoResponse>(
      new GetFullMinerInfo(props.minerId.toString(), auth.isAdmin),
      {
        onLoadingStatusChanged(isLoading) {
          if (isLoading) {
            setLoader("ALL");
          } else {
            setLoader(undefined);
          }
        },
      }
    );
    if (!res) return;
    setMinerData(res.Record.meta);
    return res.Record.meta;
  }, [props.minerId]);

  const onLoadMinerData = async (b: boolean) => {
    if (!b) return;
    if (!isExpired(props.minerId)) {
      return;
    }
    const data = await getMinerData();
    setMinerData(data);

    sessionStorage.setItem(
      `${EXPIRED_KEY}-${props.minerId}`,
      String(new Date().getTime() + 60000)
    );
  };

  return {
    loader,
    minerData,

    onClick,
    onCopyData,
    onLoadMinerData,
  };
};
